/**
 * OPP API hooks — thin wrappers around Supabase queries for OPP tables.
 */
import { getSupabaseOrNull } from '@litopia/shared/supabase';
import { useSharedAuth } from '@litopia/shared';
import type { OppLetter, OppLetterStatus, OppEnvelopeDesign } from '@/types/opp';
import {
  OP_CODE_REGEX,
  DEFAULT_LETTERS_LIMIT,
  DEFAULT_COURIER_TASKS_LIMIT,
  DEFAULT_TEMPLATES_LIMIT,
  DEFAULT_ENVELOPE_DESIGNS_LIMIT,
  DEFAULT_PUBLIC_LETTERS_LIMIT,
  DEFAULT_MUSEUM_PAGE_SIZE,
  DEFAULT_EXHIBITIONS_LIMIT,
  DEFAULT_NOTIFICATIONS_LIMIT,
  SIGNED_URL_STALE_TIME_MS,
  SIGNED_URL_GC_TIME_MS,
  NOTIFICATION_REFETCH_INTERVAL_MS,
  MAX_PHOTO_SIZE_BYTES,
  ALLOWED_IMAGE_TYPES,
  IMAGE_EXTENSION_MAP,
} from '../lib/constants';
import { useOppMutation, useOppQuery, useOppQueryClient } from '../lib/queryRuntime';

export interface OppMuseumItemSummary {
  id: string;
  title: string;
  excerpt?: string;
  category?: string;
  image_url?: string;
  view_count?: number;
  like_count?: number;
  created_at: string;
}

export interface OppMuseumExhibitionSummary {
  id: string;
  title: string;
  description?: string;
  is_featured?: boolean;
  view_count?: number;
}

export interface OppExhibitionEntrySummary {
  id: string;
  opp_museum_items: OppMuseumItemSummary | null;
}

export interface OppCommentSummary {
  id: string;
  user_id?: string | null;
  content: string;
  created_at?: string;
  is_anonymous?: boolean;
  public_profiles?: { username?: string; avatar_url?: string } | null;
}

interface OppLetterStatusLogSummary {
  id: string;
  status: string;
  created_at?: string;
  note?: string | null;
}


const OPP_SUPABASE_NOT_INITIALIZED_ERROR = new Error(
  'Supabase client not initialized. Call createSupabaseClient() first.'
);

function createNoopOppQueryResult(isSingle = false, includeCount = false) {
  return {
    data: (isSingle ? null : [] as unknown[]),
    count: includeCount ? 0 : null,
    error: null as unknown as null,
  };
}

function createNoopTableQuery() {
  let readMode = true;
  let requestCount = false;
  let singleMode = false;

  const makeChain = () => ({
    select: () => {
      readMode = true;
      return chain;
    },
    in: () => chain,
    eq: () => chain,
    neq: () => chain,
    order: () => chain,
    range: () => chain,
    limit: () => chain,
    contains: () => chain,
    is: () => chain,
    or: () => chain,
    not: () => chain,
    match: () => chain,
    filter: () => chain,
    gt: () => chain,
    gte: () => chain,
    lt: () => chain,
    lte: () => chain,
    like: () => chain,
    ilike: () => chain,
    overlaps: () => chain,
    textSearch: () => chain,
    single: () => {
      singleMode = true;
      return chain;
    },
    maybeSingle: () => {
      singleMode = true;
      return chain;
    },
    delete: () => {
      readMode = false;
      return chain;
    },
    update: () => {
      readMode = false;
      return chain;
    },
    insert: () => {
      readMode = false;
      return chain;
    },
    upsert: () => {
      readMode = false;
      return chain;
    },
    then: (resolve: (value: unknown) => unknown, reject?: (reason: unknown) => unknown) => {
      const result = readMode
        ? createNoopOppQueryResult(singleMode, requestCount)
        : { data: null, error: OPP_SUPABASE_NOT_INITIALIZED_ERROR };
      return Promise.resolve(result).then(resolve, reject);
    },
    selectWithCount: (options?: { count?: string }) => {
      if (options?.count) requestCount = true;
      return chain;
    },
  });

  const chain = makeChain();
  const proxy = new Proxy(chain as Record<string, unknown>, {
    get(target, prop) {
      if (prop === 'select') {
        return (_selection?: unknown, options?: { count?: string }) => {
          if (options?.count) requestCount = true;
          readMode = true;
          return chain;
        };
      }
      return target[prop as keyof typeof target];
    },
  });

  return proxy;
}

function createNoopOppStorage() {
  return {
    upload: async () => ({ data: null, error: OPP_SUPABASE_NOT_INITIALIZED_ERROR }),
    download: async () => ({ data: null, error: OPP_SUPABASE_NOT_INITIALIZED_ERROR }),
    remove: async () => ({ data: null, error: OPP_SUPABASE_NOT_INITIALIZED_ERROR }),
    list: async () => ({ data: null, error: OPP_SUPABASE_NOT_INITIALIZED_ERROR }),
  };
}

function getOppSupabase() {
  const supabaseClient = getSupabaseOrNull();
  if (!supabaseClient) {
    const noopChannel = {
      on: () => noopChannel,
      subscribe: () => noopChannel,
      topic: 'realtime:noop',
      unsubscribe: () => {},
      unsubscribeAll: () => {},
    };

    return {
      auth: {
        getUser: async () => ({
          data: { user: null },
          error: OPP_SUPABASE_NOT_INITIALIZED_ERROR,
        }),
      },
      from: () => createNoopTableQuery(),
      storage: {
        from: () => createNoopOppStorage(),
      },
      getChannels: () => [],
      removeChannel: () => {},
      channel: () => noopChannel,
      rpc: async () => ({ data: null, error: OPP_SUPABASE_NOT_INITIALIZED_ERROR }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any;
  }
  return supabaseClient;
}

// ---- Letters ----

export function useMyLetters(tab: 'inbox' | 'sent' | 'drafts') {
  const { user } = useSharedAuth();
  const userId = user?.id ?? null;
  return useOppQuery({
    queryKey: ['opp-letters', tab, userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID required');
      const sb = getOppSupabase();
      let q = sb.from('opp_letters').select('*');

      if (tab === 'inbox') {
        q = q.eq('recipient_id', userId).neq('status', 'draft');
      } else if (tab === 'sent') {
        q = q.eq('sender_id', userId).neq('status', 'draft');
      } else {
        q = q.eq('sender_id', userId).eq('status', 'draft');
      }

      const { data, error } = await q.order('created_at', { ascending: false }).limit(DEFAULT_LETTERS_LIMIT);
      if (error) throw error;
      return (data ?? []) as OppLetter[];
    },
    enabled: !!userId,
  });
}

export function useCreateLetter() {
  const qc = useOppQueryClient();
  return useOppMutation({
    mutationFn: async (letter: {
      id?: string;
      title?: string;
      content: string;
      style?: string;
      visibility?: string;
      type?: string;
      recipient_op_code?: string;
      status?: OppLetterStatus;
      envelope_id?: string;
      reply_to?: string;
      scheduled_at?: string;
      is_anonymous?: boolean;
      metadata?: Record<string, unknown>;
    }) => {
      const supabaseClient = getOppSupabase();
      const { data: { user }, error: authError } = await supabaseClient.auth.getUser();
      if (authError || !user) throw new Error('Not authenticated');

      const fields = {
          title: letter.title || '',
          content: letter.content,
          style: letter.style || 'classic',
          visibility: letter.visibility || 'private',
          type: letter.type || 'original',
          status: letter.status || 'draft',
          recipient_op_code: letter.recipient_op_code,
          envelope_id: letter.envelope_id,
          reply_to: letter.reply_to,
          scheduled_at: letter.scheduled_at,
          is_anonymous: letter.is_anonymous ?? false,
          metadata: letter.metadata,
      };
      const query = letter.id
        ? supabaseClient.from('opp_letters').update(fields).eq('id', letter.id).eq('sender_id', user.id).eq('status', 'draft')
        : supabaseClient.from('opp_letters').insert({ ...fields, sender_id: user.id, author_id: user.id });
      const { data, error } = await query.select().single();

      if (error) throw error;
      return data as OppLetter;
    },
    onSuccess: () => {
      qc?.invalidateQueries({ queryKey: ['opp-letters'] });
    },
  });
}

export function useUpdateLetterStatus() {
  const qc = useOppQueryClient();
  return useOppMutation({
    mutationFn: async ({ id, status }: { id: string; status: OppLetterStatus }) => {
      const { error } = await getOppSupabase()
        .from('opp_letters')
        .update({ status })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc?.invalidateQueries({ queryKey: ['opp-letters'] }),
  });
}

// ---- Museum ----

export function useMuseumItems(options: {
  theme?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
} = {}) {
  const { theme = 'all', sort = 'newest', page = 1, pageSize = DEFAULT_MUSEUM_PAGE_SIZE } = options;
  const offset = (page - 1) * pageSize;

  return useOppQuery({
    queryKey: ['opp-museum', theme, sort, page, pageSize],
    queryFn: async () => {
      const orderCol = sort === 'views' ? 'view_count'
        : sort === 'likes' ? 'like_count'
        : 'created_at';

      let q = getOppSupabase()
        .from('opp_museum_items')
        .select('*', { count: 'exact' })
        .in('status', ['public', 'approved'])
        .order(orderCol, { ascending: false })
        .range(offset, offset + pageSize - 1);

      if (theme && theme !== 'all') {
        q = q.contains('tags', [theme]);
      }

      const { data, error, count } = await q;
      if (error) throw error;
      return { items: (data ?? []) as OppMuseumItemSummary[], total: count ?? 0 };
    },
    placeholderData: (prev) => prev,
  });
}

export function useMuseumExhibitions() {
  return useOppQuery({
    queryKey: ['opp-exhibitions'],
    queryFn: async () => {
      const { data, error } = await getOppSupabase()
        .from('opp_museum_exhibitions')
        .select('*')
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(DEFAULT_EXHIBITIONS_LIMIT);
      if (error) throw error;
      return (data ?? []) as OppMuseumExhibitionSummary[];
    },
  });
}

export function useExhibitionEntries(exhibitionId?: string) {
  return useOppQuery({
    queryKey: ['opp-exhibition-entries', exhibitionId],
    queryFn: async () => {
      if (!exhibitionId) throw new Error('Exhibition ID required');
      const { data, error } = await getOppSupabase()
        .from('opp_museum_exhibition_entries')
        .select('*, opp_museum_items(*)')
        .eq('exhibition_id', exhibitionId)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as OppExhibitionEntrySummary[];
    },
    enabled: !!exhibitionId,
  });
}

// ---- Courier ----

export function useOppSchools() {
  return useOppQuery({
    queryKey: ['opp-schools'],
    queryFn: async () => {
      const { data, error } = await getOppSupabase()
        .from('opp_op_schools')
        .select('school_code, school_name')
        .eq('is_active', true)
        .order('school_name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as { school_code: string; school_name: string }[];
    },
    staleTime: 1000 * 60 * 10, // 10 minutes - reference data changes infrequently
  });
}

export function useOppAreas(schoolCode?: string) {
  return useOppQuery({
    queryKey: ['opp-areas', schoolCode],
    queryFn: async () => {
      if (!schoolCode) return [];
      const { data, error } = await getOppSupabase()
        .from('opp_op_areas')
        .select('area_code, area_name')
        .eq('school_code', schoolCode)
        .eq('is_active', true)
        .order('area_name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as { area_code: string; area_name: string }[];
    },
    enabled: !!schoolCode,
    staleTime: 1000 * 60 * 10,
  });
}

export function useMyCourier() {
  const { user } = useSharedAuth();
  const userId = user?.id ?? null;
  return useOppQuery({
    queryKey: ['opp-courier', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID required');
      const { data, error } = await getOppSupabase()
        .from('opp_couriers')
        .select('*')
        .eq('user_id', userId)
        // A user may have no courier record yet, and legacy data can contain
        // more than one row. Avoid PostgREST's singular-response 406 in both
        // cases by requesting a bounded array and selecting the newest row.
        .order('created_at', { ascending: false })
        .limit(1);
      if (error) throw error;
      return data?.[0] ?? null;
    },
    enabled: !!userId,
  });
}

export function useCourierTasks(courierId?: string) {
  return useOppQuery({
    queryKey: ['opp-courier-tasks', courierId],
    queryFn: async () => {
      if (!courierId) throw new Error('Courier ID required');
      const { data, error } = await getOppSupabase()
        .from('opp_courier_tasks')
        .select('*')
        .eq('courier_id', courierId)
        .order('created_at', { ascending: false })
        .limit(DEFAULT_COURIER_TASKS_LIMIT);
      if (error) throw error;
      return (data ?? []) as CourierTask[];
    },
    enabled: !!courierId,
  });
}

type CourierTask = {
  id: string;
  status: string;
  created_at: string;
  completed_at?: string | null;
  [key: string]: unknown;
};

export function useApplyCourier() {
  const qc = useOppQueryClient();
  return useOppMutation({
    mutationFn: async (application: {
      name: string;
      province_code: string;
      city_code: string;
      district_code: string;
      school_code: string;
      area_code?: string;
      transport_method?: string;
      weekly_hours?: number;
      self_intro?: string;
      phone?: string;
    }) => {
      const sb = getOppSupabase();
      const { data: { user }, error: authError } = await sb.auth.getUser();
      if (authError || !user) throw new Error('Not authenticated');

      const { data, error } = await sb
        .from('opp_couriers')
        .insert({
          user_id: user.id,
          name: application.name,
          province_code: application.province_code,
          city_code: application.city_code,
          district_code: application.district_code,
          school_code: application.school_code,
          area_code: application.area_code?.trim() || null,
          transport_method: application.transport_method || 'walk',
          weekly_hours: application.weekly_hours || 0,
          self_intro: application.self_intro?.trim() || null,
          phone: application.phone?.trim() || null,
          status: 'pending',
          level: 1,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc?.invalidateQueries({ queryKey: ['opp-courier'] });
    },
  });
}

export function useUpdateTaskStatus() {
  const qc = useOppQueryClient();
  return useOppMutation({
    mutationFn: async ({ taskId, status }: { taskId: string; status: string }) => {
      const { error } = await getOppSupabase().rpc('opp_update_courier_task_status', {
        p_task_id: taskId,
        p_status: status,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      // The server owns transition validation; refresh only after it confirms success.
      qc?.invalidateQueries({ queryKey: ['opp-courier-tasks'] });
    },
  });
}

// ---- Letter Templates ----

export function useLetterTemplates(category?: string) {
  return useOppQuery({
    queryKey: ['opp-letter-templates', category],
    queryFn: async () => {
      let q = getOppSupabase()
        .from('opp_letter_templates')
        .select('*')
        .eq('is_active', true)
        .eq('is_public', true)
        .order('usage_count', { ascending: false })
        .limit(DEFAULT_TEMPLATES_LIMIT);

      if (category && category !== 'all') {
        q = q.eq('category', category);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        name: string;
        description?: string;
        content_template?: string;
        style?: string;
      }>;
    },
  });
}

// ---- Letter Likes ----

export function useLetterLikes(letterId?: string) {
  return useOppQuery({
    queryKey: ['opp-letter-likes', letterId],
    queryFn: async () => {
      if (!letterId) throw new Error('Letter ID required');
      const { count, error } = await getOppSupabase()
        .from('opp_letter_likes')
        .select('*', { count: 'exact', head: true })
        .eq('letter_id', letterId);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!letterId,
  });
}

export function useToggleLike() {
  const qc = useOppQueryClient();
  return useOppMutation({
    mutationFn: async ({ letterId, userId }: { letterId: string; userId: string }) => {
      const sb = getOppSupabase();
      const { data: existing, error: lookupError } = await sb
        .from('opp_letter_likes')
        .select('id')
        .eq('letter_id', letterId)
        .eq('user_id', userId)
        .maybeSingle();
      if (lookupError) throw lookupError;

      if (existing) {
        const { error: deleteError } = await sb
          .from('opp_letter_likes')
          .delete()
          .eq('id', existing.id);
        if (deleteError) throw deleteError;
        return { liked: false };
      }

      const { error: insertError } = await sb
        .from('opp_letter_likes')
        .insert({ letter_id: letterId, user_id: userId });
      if (insertError) throw insertError;
      return { liked: true };
    },
    onSuccess: (_, { letterId }) => {
      qc?.invalidateQueries({ queryKey: ['opp-letter-likes', letterId] });
      qc?.invalidateQueries({ queryKey: ['opp-letters'] });
      qc?.invalidateQueries({ queryKey: ['opp-public-letters'] });
      qc?.invalidateQueries({ queryKey: ['opp-my-liked-letters'] });
    },
  });
}

// ---- Letter Status Logs ----

export function useLetterStatusLogs(letterId?: string) {
  return useOppQuery({
    queryKey: ['opp-letter-status-logs', letterId],
    queryFn: async () => {
      if (!letterId) throw new Error('Letter ID required');
      const { data, error } = await getOppSupabase()
        .from('opp_letter_status_logs')
        .select('*')
        .eq('letter_id', letterId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as OppLetterStatusLogSummary[];
    },
    enabled: !!letterId,
  });
}

// ---- Comments ----

export function useComments(targetId: string, targetType: string) {
  return useOppQuery({
    queryKey: ['opp-comments', targetId, targetType],
    queryFn: async () => {
      const { data, error } = await getOppSupabase()
        .from('opp_comments')
        .select('*, public_profiles!user_id(username, avatar_url)')
        .eq('target_id', targetId)
        .eq('target_type', targetType)
        .is('parent_id', null)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as OppCommentSummary[];
    },
    enabled: !!targetId,
  });
}

export function useCreateComment() {
  const qc = useOppQueryClient();
  return useOppMutation({
    mutationFn: async (comment: {
      target_id: string;
      target_type: string;
      content: string;
      parent_id?: string;
      is_anonymous?: boolean;
    }) => {
      const sb = getOppSupabase();
      const { data: { user }, error: authError } = await sb.auth.getUser();
      if (authError || !user) throw new Error('Not authenticated');

      const { data, error } = await sb
        .from('opp_comments')
        .insert({ ...comment, user_id: user.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, { target_id, target_type }) => {
      qc?.invalidateQueries({ queryKey: ['opp-comments', target_id, target_type] });
    },
  });
}

// ---- OP Code Applications ----

export function useMyOpApplication() {
  const { user } = useSharedAuth();
  return useOppQuery({
    queryKey: ['opp-op-application', user?.id],
    queryFn: async () => {
      const sb = getOppSupabase();
      if (!user) return null;

      const { data, error } = await sb
        .from('opp_op_applications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
    enabled: !!user,
  });
}

export function useSubmitOpApplication() {
  const qc = useOppQueryClient();
  return useOppMutation({
    mutationFn: async (application: {
      school_code: string;
      area_code: string;
      point_code: string;
      point_name?: string;
      reason?: string;
    }) => {
      const sb = getOppSupabase();
      const { data: { user }, error: authError } = await sb.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('Not authenticated');

      // Check for existing pending application
      const { data: existing, error: lookupError } = await sb
        .from('opp_op_applications')
        .select('id')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .maybeSingle();
      if (lookupError) throw lookupError;

      if (existing) {
        throw new Error('您已有一个待审核的 OP Code 申请，请等待审核结果后再提交');
      }

      const { data, error } = await sb
        .from('opp_op_applications')
        .insert({ ...application, user_id: user.id, status: 'pending' })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc?.invalidateQueries({ queryKey: ['opp-op-application'] });
    },
  });
}

// ---- Envelope Designs ----

export function useEnvelopeDesigns(userOpCode?: string) {
  return useOppQuery({
    queryKey: ['opp-envelope-designs', userOpCode],
    queryFn: async () => {
      let q = getOppSupabase()
        .from('opp_envelope_designs')
        .select('*')
        .eq('status', 'approved')
        .eq('is_active', true)
        .order('vote_count', { ascending: false })
        .limit(DEFAULT_ENVELOPE_DESIGNS_LIMIT);

      // Validate OP Code format before using it
      if (userOpCode && OP_CODE_REGEX.test(userOpCode)) {
        const schoolPrefix = userOpCode.slice(0, 2);
        q = q.or(
          `supported_op_prefix.is.null,type.eq.city,supported_op_prefix.eq.${schoolPrefix}`
        );
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as OppEnvelopeDesign[];
    },
  });
}

export function useMyEnvelopeDesigns() {
  const { user } = useSharedAuth();
  const userId = user?.id ?? null;
  return useOppQuery({
    queryKey: ['opp-my-envelope-designs', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID required');
      const { data, error } = await getOppSupabase()
        .from('opp_envelope_designs')
        .select('*')
        .eq('creator_id', userId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as OppEnvelopeDesign[];
    },
    enabled: !!userId,
  });
}

export function useCreateEnvelopeDesign() {
  const qc = useOppQueryClient();
  return useOppMutation({
    mutationFn: async (design: {
      school_code?: string;
      type?: 'city' | 'campus';
      theme?: string;
      image_url?: string;
      thumbnail_url?: string;
      description?: string;
      supported_op_prefix?: string;
    }) => {
      const sb = getOppSupabase();
      const { data: { user }, error: authError } = await sb.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await sb
        .from('opp_envelope_designs')
        .insert({
          creator_id: user.id,
          school_code: design.school_code,
          type: design.type || 'campus',
          theme: design.theme,
          image_url: design.image_url,
          thumbnail_url: design.thumbnail_url,
          description: design.description,
          supported_op_prefix: design.supported_op_prefix,
          status: 'pending',
        })
        .select()
        .single();
      if (error) throw error;
      return data as OppEnvelopeDesign;
    },
    onSuccess: () => {
      qc?.invalidateQueries({ queryKey: ['opp-envelope-designs'] });
      qc?.invalidateQueries({ queryKey: ['opp-my-envelope-designs'] });
    },
  });
}

export function useVoteForDesign() {
  const qc = useOppQueryClient();
  return useOppMutation({
    mutationFn: async ({ designId }: { designId: string }) => {
      const sb = getOppSupabase();
      const { data: { user }, error: authError } = await sb.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('Not authenticated');

      const { data: existing, error: lookupError } = await sb
        .from('opp_envelope_votes')
        .select('id')
        .eq('design_id', designId)
        .eq('user_id', user.id)
        .maybeSingle();
      if (lookupError) throw lookupError;

      if (existing) {
        // Remove vote (trigger auto-updates vote_count)
        const { error: deleteError } = await sb
          .from('opp_envelope_votes')
          .delete()
          .eq('id', existing.id);
        if (deleteError) throw deleteError;
        return { voted: false };
      }

      // The database trigger owns the denormalized vote_count update.
      const { error } = await sb
        .from('opp_envelope_votes')
        .insert({ design_id: designId, user_id: user.id });
      if (error) throw error;
      return { voted: true };
    },
    onSuccess: () => {
      qc?.invalidateQueries({ queryKey: ['opp-envelope-designs'] });
      qc?.invalidateQueries({ queryKey: ['opp-my-envelope-designs'] });
    },
  });
}

// ---- Museum Collections ----

export function useMyCollections() {
  const { user } = useSharedAuth();
  return useOppQuery({
    queryKey: ['opp-my-collections', user?.id],
    queryFn: async () => {
      const sb = getOppSupabase();
      if (!user) return [];

      const { data, error } = await sb
        .from('opp_museum_item_saves')
        .select('*, opp_museum_items(*)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });
}

export function useToggleCollection() {
  const qc = useOppQueryClient();
  return useOppMutation({
    mutationFn: async ({ itemId, userId }: { itemId: string; userId: string }) => {
      const sb = getOppSupabase();
      const { data: existing, error: lookupError } = await sb
        .from('opp_museum_item_saves')
        .select('id')
        .eq('item_id', itemId)
        .eq('user_id', userId)
        .maybeSingle();
      if (lookupError) throw lookupError;

      if (existing) {
        const { error: deleteError } = await sb
          .from('opp_museum_item_saves')
          .delete()
          .eq('id', existing.id);
        if (deleteError) throw deleteError;
        return { collected: false };
      }

      const { error: insertError } = await sb
        .from('opp_museum_item_saves')
        .insert({ item_id: itemId, user_id: userId });
      if (insertError) throw insertError;
      return { collected: true };
    },
    onSuccess: () => {
      qc?.invalidateQueries({ queryKey: ['opp-my-collections'] });
      qc?.invalidateQueries({ queryKey: ['opp-museum'] }); // refresh museum list to reflect updated save state
    },
  });
}

// ---- Public Letters (Plaza / Home preview) ----

export type PublicLetterFeedOrder = 'recommended' | 'latest';

export interface UsePublicLettersOptions {
  limit?: number;
  order?: PublicLetterFeedOrder;
  enabled?: boolean;
}

export function usePublicLetters(options: UsePublicLettersOptions = {}) {
  const {
    limit = DEFAULT_PUBLIC_LETTERS_LIMIT,
    order = 'latest',
    enabled = true,
  } = options;
  return useOppQuery({
    queryKey: ['opp-public-letters', order, limit],
    enabled,
    queryFn: async () => {
      let query = getOppSupabase()
        .from('opp_letters')
        .select('*')
        .eq('visibility', 'public')
        .in('status', ['approved', 'delivered', 'read']);

      // Recommendation is intentionally deterministic: engagement is the
      // primary signal and recency breaks ties. The latest feed is purely
      // chronological, so the two visible navigation choices change content.
      if (order === 'recommended') {
        query = query.order('like_count', { ascending: false, nullsFirst: false });
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as OppLetter[];
    },
  });
}

export function useMyLikedLetterIds() {
  const { user } = useSharedAuth();
  return useOppQuery({
    queryKey: ['opp-my-liked-letters', user?.id],
    queryFn: async () => {
      if (!user) return new Set<string>();
      const { data, error } = await getOppSupabase()
        .from('opp_letter_likes')
        .select('letter_id')
        .eq('user_id', user.id);
      if (error) throw error;
      return new Set((data ?? []).map((row: { letter_id: string }) => row.letter_id));
    },
    enabled: !!user,
  });
}

export function useFollowingLetters(limit = 20, enabled = true) {
  const { user } = useSharedAuth();
  return useOppQuery({
    queryKey: ['opp-following-letters', user?.id, limit],
    enabled: !!user && enabled,
    queryFn: async () => {
      if (!user) throw new Error('User required');
      const { data: followings, error: followingsError } = await getOppSupabase()
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id);
      if (followingsError) throw followingsError;

      const ids = (followings ?? []).map((row: { following_id: string }) => row.following_id);
      if (ids.length === 0) return [] as OppLetter[];

      const { data, error } = await getOppSupabase()
        .from('opp_letters')
        .select('*')
        .eq('visibility', 'public')
        .in('status', ['approved', 'delivered', 'read'])
        .in('sender_id', ids)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return (data ?? []) as OppLetter[];
    },
  });
}

// ---- User Stats ----

export function useMyStats(userId?: string) {
  return useOppQuery({
    queryKey: ['opp-user-stats', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID required');
      const { data, error } = await getOppSupabase()
        .from('opp_user_stats')
        .select('*')
        .eq('user_id', userId)
        .limit(1);
      if (error) throw error;
      return data?.[0] ?? null;
    },
    enabled: !!userId,
  });
}

// ---- Notifications ----

export function useUnreadNotificationCount() {
  const { user } = useSharedAuth();
  return useOppQuery({
    queryKey: ['opp-unread-notifications', user?.id],
    queryFn: async () => {
      const sb = getOppSupabase();
      if (!user) return 0;
      const { count, error } = await sb
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', user.id)
        .eq('is_read', false)
        .in('type', ['opp_letter', 'opp_courier', 'opp_system', 'opp_upgrade']);
      if (error) return 0;
      return count ?? 0;
    },
    staleTime: NOTIFICATION_REFETCH_INTERVAL_MS,
    refetchInterval: NOTIFICATION_REFETCH_INTERVAL_MS * 4, // 2 minutes - poll every 2 minutes (reduced from 30s)
    enabled: !!user,
  });
}

export function useNotifications(limit = DEFAULT_NOTIFICATIONS_LIMIT) {
  const { user } = useSharedAuth();
  return useOppQuery({
    queryKey: ['opp-notifications', user?.id, limit],
    queryFn: async () => {
      const sb = getOppSupabase();
      if (!user) return [];
      const { data, error } = await sb
        .from('notifications')
        .select('*')
        .eq('recipient_id', user.id)
        .in('type', ['opp_letter', 'opp_courier', 'opp_system', 'opp_upgrade'])
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });
}

export function useMarkNotificationRead() {
  const qc = useOppQueryClient();
  return useOppMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await getOppSupabase()
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('id', notificationId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc?.invalidateQueries({ queryKey: ['opp-unread-notifications'] });
      qc?.invalidateQueries({ queryKey: ['opp-notifications'] });
    },
  });
}

// ---- Letter Threads ----

export function useLetterThread(letterId?: string) {
  return useOppQuery({
    queryKey: ['opp-letter-thread', letterId],
    queryFn: async () => {
      if (!letterId) throw new Error('Letter ID required');
      const { data, error } = await getOppSupabase()
        .from('opp_letter_threads')
        .select('*')
        .eq('original_letter', letterId)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
    enabled: !!letterId,
  });
}

// ---- Letter Shares ----

export function useShareLetter() {
  const qc = useOppQueryClient();
  return useOppMutation({
    mutationFn: async ({ letterId, platform }: { letterId: string; platform: string }) => {
      const sb = getOppSupabase();
      const { data: { user }, error: authError } = await sb.auth.getUser();
      if (authError || !user) throw new Error('Not authenticated');

      const { data, error } = await sb
        .from('opp_letter_shares')
        .insert({ letter_id: letterId, user_id: user.id, platform })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, { letterId }) => {
      qc?.invalidateQueries({ queryKey: ['opp-letter-shares', letterId] });
    },
  });
}

export function useLetterShareCount(letterId?: string) {
  return useOppQuery({
    queryKey: ['opp-letter-shares', letterId],
    queryFn: async () => {
      if (!letterId) throw new Error('Letter ID required');
      const { count, error } = await getOppSupabase()
        .from('opp_letter_shares')
        .select('*', { count: 'exact', head: true })
        .eq('letter_id', letterId);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!letterId,
  });
}

// ---- Courier Upgrades ----

export function useMyUpgradeRequest(courierId?: string) {
  return useOppQuery({
    queryKey: ['opp-courier-upgrade', courierId],
    queryFn: async () => {
      if (!courierId) throw new Error('Courier ID required');
      const { data, error } = await getOppSupabase()
        .from('opp_courier_upgrades')
        .select('*')
        .eq('courier_id', courierId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
    enabled: !!courierId,
  });
}

export function useRequestUpgrade() {
  const qc = useOppQueryClient();
  return useOppMutation({
    mutationFn: async ({ courierId, currentLevel, requestLevel, reason }: {
      courierId: string;
      currentLevel: number;
      requestLevel: number;
      reason?: string;
    }) => {
      const sb = getOppSupabase();

      // Check for existing pending request before insert
      const { data: existing, error: lookupError } = await sb
        .from('opp_courier_upgrades')
        .select('id, status')
        .eq('courier_id', courierId)
        .eq('status', 'pending')
        .maybeSingle();
      if (lookupError) throw lookupError;

      if (existing) {
        throw new Error('您已有一个待审核的升级申请，请等待审核结果后再提交');
      }

      const { data, error } = await sb
        .from('opp_courier_upgrades')
        .insert({ courier_id: courierId, current_level: currentLevel, request_level: requestLevel, reason, status: 'pending' })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, { courierId }) => {
      qc?.invalidateQueries({ queryKey: ['opp-courier-upgrade', courierId] });
    },
  });
}

// ---- Letter Photos ----

export function useLetterPhotos(letterId?: string) {
  return useOppQuery({
    queryKey: ['opp-letter-photos', letterId],
    queryFn: async () => {
      if (!letterId) throw new Error('Letter ID required');
      const { data, error } = await getOppSupabase()
        .from('opp_letter_photos')
        .select('*')
        .eq('letter_id', letterId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!letterId,
  });
}

export function useUploadLetterPhoto() {
  const qc = useOppQueryClient();
  return useOppMutation({
    mutationFn: async ({ letterId, imageUrl, isPublic }: {
      letterId: string;
      imageUrl: string;
      isPublic?: boolean;
    }) => {
      const { data, error } = await getOppSupabase()
        .from('opp_letter_photos')
        .insert({ letter_id: letterId, image_url: imageUrl, is_public: isPublic ?? false })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, { letterId }) => {
      qc?.invalidateQueries({ queryKey: ['opp-letter-photos', letterId] });
    },
  });
}

export function useUploadLetterPhotoFile() {
  const { user } = useSharedAuth();
  const qc = useOppQueryClient();

  return useOppMutation({
    mutationFn: async ({
      letterId,
      file,
      isPublic = false,
    }: {
      letterId: string;
      file: File;
      isPublic?: boolean;
    }) => {
      if (!user) throw new Error('Not authenticated');

      if (!ALLOWED_IMAGE_TYPES.includes(file.type as typeof ALLOWED_IMAGE_TYPES[number])) throw new Error('不支持的文件类型，请上传 JPG、PNG、WebP 或 GIF');

      if (file.size > MAX_PHOTO_SIZE_BYTES) throw new Error('文件过大，最大支持 5MB');

      const ext = IMAGE_EXTENSION_MAP[file.type] ?? 'jpg';
      const randomSuffix = crypto.randomUUID().replace(/-/g, '').slice(0, 8);
      const path = `${user.id}/${letterId}/${Date.now()}-${randomSuffix}.${ext}`;

      const { error: uploadError } = await getOppSupabase().storage
        .from('opp-letter-photos')
        .upload(path, file, { upsert: false });

      if (uploadError) throw uploadError;

      const { data, error } = await getOppSupabase()
        .from('opp_letter_photos')
        .insert({ letter_id: letterId, image_url: path, is_public: isPublic })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, { letterId }) => {
      qc?.invalidateQueries({ queryKey: ['opp-letter-photos', letterId] });
    },
  });
}

export function useLetterPhotoSignedUrl(path: string | null | undefined) {
  return useOppQuery({
    queryKey: ['opp_letter_photo_url', path],
    queryFn: async () => {
      if (!path) throw new Error('No path');
      if (path.startsWith('http')) return path;
      const { data, error } = await getOppSupabase().storage
        .from('opp-letter-photos')
        .createSignedUrl(path, 60 * 60 * 24 * 7);
      if (error) throw error;
      return data.signedUrl;
    },
    enabled: !!path,
    staleTime: SIGNED_URL_STALE_TIME_MS,
    gcTime: SIGNED_URL_GC_TIME_MS,
  });
}

// ---- Letter User Marks (starred / archived) ----

export function useLetterMarks(letterId?: string) {
  const { user } = useSharedAuth();
  return useOppQuery({
    queryKey: ['opp_letter_marks', user?.id, letterId],
    enabled: !!user && !!letterId,
    queryFn: async () => {
      if (!user) throw new Error('User required');
      if (!letterId) throw new Error('Letter ID required');
      const { data, error } = await getOppSupabase()
        .from('opp_letter_user_marks')
        .select('is_starred, is_archived')
        .eq('user_id', user.id)
        .eq('letter_id', letterId)
        .maybeSingle();
      if (error) throw error;
      return data ?? { is_starred: false, is_archived: false };
    },
  });
}

export function useAllMarkedLetters(type: 'starred' | 'archived') {
  const { user } = useSharedAuth();
  return useOppQuery({
    queryKey: ['opp_marked_letters', user?.id, type],
    enabled: !!user,
    queryFn: async () => {
      if (!user) throw new Error('User required');
      const col = type === 'starred' ? 'is_starred' : 'is_archived';
      const { data, error } = await getOppSupabase()
        .from('opp_letter_user_marks')
        .select(`
          letter_id,
          is_starred,
          is_archived,
          letter:opp_letters!letter_id(*)
        `)
        .eq('user_id', user.id)
        .eq(col, true)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? [])
        .map((row: Record<string, unknown>) => row.letter)
        .filter(Boolean) as OppLetter[];
    },
  });
}

export function useToggleLetterMark() {
  const { user } = useSharedAuth();
  const queryClient = useOppQueryClient();

  return useOppMutation({
    mutationFn: async ({
      letterId,
      field,
      value,
    }: {
      letterId: string;
      field: 'is_starred' | 'is_archived';
      value: boolean;
    }) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await getOppSupabase()
        .from('opp_letter_user_marks')
        .upsert(
          { user_id: user.id, letter_id: letterId, [field]: value },
          { onConflict: 'user_id,letter_id' }
        )
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, { letterId }) => {
      queryClient?.invalidateQueries({ queryKey: ['opp_letter_marks', user?.id, letterId] });
      queryClient?.invalidateQueries({ queryKey: ['opp_marked_letters', user?.id] });
    },
  });
}
